import React, {ReactElement, ReactNode, useEffect, useState} from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  Typography,
  Link
} from '@material-ui/core';
import {useForm, Controller, SubmitHandler} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {useDispatch} from 'react-redux';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import moment from 'moment';
import {
  fetchGuarantorInfoForm,
  fetchGuarantorInfoPrefill,
  saveGuarantorInfoForm
} from '@shared/services/Payment.service';
import {getInsuranceFile} from '@shared/services/AppointmentSchedulingService';
import {logoutGuarantor} from '@shared/services/CommonServices';
import {ReferenceStateListData} from '@shared/services/service-mappers/ReferenceDataMapper';
import {_guarantorLogin, _setPatientPaymentsToken} from '@shared/store/actions/PaymentActions';
import {_guarantorLogout} from '@shared/store/actions/LogoutActions';
import './GuarantorInfo.scss';
import MedopsLogo from '@components/system/MyIcons/MedopsLogo';
// import ClinicGroupsIcon from '@components/system/MyIcons/ClinicGroupsIcon';
import {environment} from '@shared/environment/environment';
import HelperUtils from '@shared/utils/HelperUtils';
import emailValidator from '@shared/utils/Validate';
import PhoneFormatter from '@components/system/PhoneFormatter/PhoneFormatter';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import DateTimePickerWrapper from '@components/system/CustomDateTimePicker/DateTimePickerWrapper';
import PaymentsStyledInput from '@components/system/FormInput/PaymentsStyledInput';
import CloseIcon from '@material-ui/icons/Close';
import Loader from '@components/framework/loader/Loader';
import PublicPageLayout from '../PublicPageLayout/PublicPageLayout';
import NavRoutes from 'src/constants/route/NavRoutes';

/** Single query param used to return to guarantor info after portal login. */
const GUARANTOR_INFO_RETURN_TO_PARAM = 'returnTo';

// ─── Types ────────────────────────────────────────────────────────────────────

type GuarantorInfoParams = {
  token: string;
};

type GuarantorInfoFields = {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
  gender: string;
  dob: moment.Moment | null;
  email: string;
  patient_email: string;
  use_guarantor_email: boolean;
};

type PrefilledInsuranceCard = {
  id: number;
  patient_id: number;
  file_id1: string;
  file_id2: string;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const defaultValues: GuarantorInfoFields = {
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  zip_code: '',
  gender: '',
  dob: null,
  email: '',
  patient_email: '',
  use_guarantor_email: false
};

const sexOptions = [
  {label: 'Male', value: 'male'},
  {label: 'Female', value: 'female'},
  {label: 'Other', value: 'other'}
];

const PAYMENT_TOKEN_LENGTH = 8;
const GUARANTOR_INFO_PORTAL_LOGIN_KEY = 'guarantorInfoFromPortalLogin';
const GUARANTOR_INFO_PAYMENT_TOKEN_KEY = 'guarantorInfoPaymentToken';
const GUARANTOR_INFO_RETURN_PATH_KEY = 'guarantorInfoReturnPath';

function parsePaymentTokenFromGuarantorInfoToken(compositeToken: string): string {
  const normalized = String(compositeToken || '').trim().toLowerCase();
  if (normalized.length <= PAYMENT_TOKEN_LENGTH) {
    return normalized;
  }
  return normalized.slice(0, PAYMENT_TOKEN_LENGTH);
}

function arrayBufferToPreviewUrl(buffer: ArrayBuffer): string {
  return URL.createObjectURL(new Blob([buffer]));
}

// ─── Component ────────────────────────────────────────────────────────────────

const GuarantorInfo = (): ReactElement => {
  const {token} = useParams<GuarantorInfoParams>();
  const history = useHistory();
  const location = useLocation();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const dispatch = useDispatch();

  const shouldPrefill =
    new URLSearchParams(location.search).get('prefill') === '1';
  const fromPortalLogin =
    sessionStorage.getItem(GUARANTOR_INFO_PORTAL_LOGIN_KEY) === '1';
  const paymentTokenForDashboard =
    sessionStorage.getItem(GUARANTOR_INFO_PAYMENT_TOKEN_KEY) ||
    parsePaymentTokenFromGuarantorInfoToken(token);
  const guarantorInfoReturnPath = `/${NavRoutes.PUBLIC.GUARANTOR_INFO}/${token}`;
  const portalLoginUrl = `/pay/${parsePaymentTokenFromGuarantorInfoToken(
    token
  )}?${GUARANTOR_INFO_RETURN_TO_PARAM}=${encodeURIComponent(guarantorInfoReturnPath)}`;

  const [prefilledInsurance, setPrefilledInsurance] =
    useState<PrefilledInsuranceCard | null>(null);
  const [loadingInsurancePreviews, setLoadingInsurancePreviews] = useState(false);
  const [apiResponseData, setApiResponseData] = useState<any>(null);
  const [insuranceFiles, setInsuranceFiles] = useState<{front: File | null; back: File | null}>({front: null, back: null});
  const [insurancePreviews, setInsurancePreviews] = useState<{front: string | null; back: string | null}>({front: null, back: null});
  const [insuranceInputKeys, setInsuranceInputKeys] = useState({front: 0, back: 0});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [pageError, setPageError] = useState('');
  const [statesList, setStatesList] = useState<ReferenceStateListData[]>([]);

  const hasInsurance = fromPortalLogin && Boolean(prefilledInsurance);

  // ─── React Hook Form ──────────────────────────────────────────────────────

  const {control, handleSubmit, errors, reset, setValue, watch} = useForm<GuarantorInfoFields>({
    mode: 'all',
    shouldFocusError: false,
    defaultValues,
    resolver: async (values) => {
      const errorsObj: any = {};

      if (!values.address_line_1?.trim()) {
        errorsObj.address_line_1 = {message: 'Address is required.'};
      }
      if (!values.city?.trim()) {
        errorsObj.city = {message: 'City is required.'};
      }
      if (!values.state?.trim()) {
        errorsObj.state = {message: 'State is required.'};
      }
      if (!values.zip_code?.trim()) {
        errorsObj.zip_code = {message: 'ZIP code is required.'};
      } else if (!/^\d{5}$/.test(values.zip_code.trim())) {
        errorsObj.zip_code = {message: 'Must be 5 digits.'};
      }
      if (!values.gender) {
        errorsObj.gender = {message: 'Sex is required.'};
      }
      if (!values.dob) {
        errorsObj.dob = {message: 'Date of birth is required.'};
      } else if (
        !values.dob.isValid() ||
        values.dob.format('YYYY') < '1900' ||
        values.dob.isAfter(moment())
      ) {
        errorsObj.dob = {message: 'Enter a valid date of birth.'};
      }
      if (values.use_guarantor_email) {
        if (!values.email?.trim()) {
          errorsObj.email = {message: 'Enter the guarantor email to use it for the patient.'};
        } else if (!emailValidator(values.email.trim())) {
          errorsObj.email = {message: 'Enter a valid email address.'};
        }
      } else if (!values.patient_email?.trim()) {
        errorsObj.patient_email = {message: "Enter the patient's email."};
      } else if (!emailValidator(values.patient_email.trim())) {
        errorsObj.patient_email = {message: 'Enter a valid email address.'};
      } else if (values.email?.trim() && !emailValidator(values.email.trim())) {
        errorsObj.email = {message: 'Enter a valid email address.'};
      }

      return {values, errors: errorsObj};
    }
  });

  // ─── Load form (token verify) + states ─────────────────────────────────────

  useEffect(() => {
    let mounted = true;

    async function loadForm() {
      try {
        const response = await fetchGuarantorInfoForm({token});
        if (!mounted) return;

        setApiResponseData(response?.data);
        if (Array.isArray(response?.data?.states)) {
          setStatesList(response.data.states as ReferenceStateListData[]);
        }

        reset({
          ...defaultValues,
          patient_email: response?.data?.patient?.email || ''
        });
        if (response?.data?.is_guarantor_info_submitted) {
          setAlreadySubmitted(true);
        }
      } catch (error) {
        if (mounted) setPageError(error?.message || 'Unable to load this form.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadForm();
    return () => { mounted = false; };
  }, [token, reset]);

  useEffect(() => {
    if (!shouldPrefill || loading || !fromPortalLogin) {
      return undefined;
    }

    const patientId = apiResponseData?.patient?.id;
    if (!patientId) {
      return undefined;
    }

    let mounted = true;

    async function loadPrefill() {
      try {
        const response = await fetchGuarantorInfoPrefill({
          token,
          patient_id: patientId
        });
        if (!mounted) {
          return;
        }

        const guarantor = response?.data?.guarantor;
        if (guarantor) {
          const guarantorEmail = guarantor.email || '';
          const patientEmail = apiResponseData?.patient?.email || '';
          const sameEmail =
            Boolean(guarantorEmail.trim()) &&
            guarantorEmail.trim().toLowerCase() === patientEmail.trim().toLowerCase();
          reset({
            address_line_1: guarantor.address_line_1 || '',
            address_line_2: guarantor.address_line_2 || '',
            city: guarantor.city || '',
            state: guarantor.state || '',
            zip_code: guarantor.zip_code || '',
            gender: guarantor.gender || '',
            dob: guarantor.dob ? moment(guarantor.dob) : null,
            email: guarantorEmail,
            patient_email: sameEmail ? guarantorEmail : patientEmail,
            use_guarantor_email: sameEmail
          });
        }

        const insuranceList = Array.isArray(response?.data?.insurance)
          ? response.data.insurance
          : [];
        const patientInsurance =
          insuranceList.find(
            (item: PrefilledInsuranceCard) =>
              item?.file_id1 && item?.file_id2
          ) || null;

        if (patientInsurance) {
          setPrefilledInsurance(patientInsurance);
          setLoadingInsurancePreviews(true);
          try {
            const [frontBuffer, backBuffer] = await Promise.all([
              getInsuranceFile(patientInsurance.file_id1),
              getInsuranceFile(patientInsurance.file_id2)
            ]);
            if (!mounted) {
              return;
            }
            setInsurancePreviews(current => {
              if (current.front) {
                URL.revokeObjectURL(current.front);
              }
              if (current.back) {
                URL.revokeObjectURL(current.back);
              }
              return {
                front: arrayBufferToPreviewUrl(frontBuffer),
                back: arrayBufferToPreviewUrl(backBuffer)
              };
            });
          } catch (insuranceError) {
            if (mounted) {
              enqueueSnackbar(
                insuranceError?.message || 'Unable to load insurance card images.',
                {variant: 'error'}
              );
            }
          } finally {
            if (mounted) {
              setLoadingInsurancePreviews(false);
            }
          }
        }

        if (location.search.includes('prefill=1')) {
          history.replace(`/${NavRoutes.PUBLIC.GUARANTOR_INFO}/${token}`);
        }
      } catch (error) {
        if (mounted) {
          enqueueSnackbar(
            error?.message || 'Unable to load your guarantor details.',
            {variant: 'error'}
          );
        }
      }
    }

    loadPrefill();
    return () => {
      mounted = false;
    };
  }, [
    shouldPrefill,
    loading,
    fromPortalLogin,
    token,
    reset,
    history,
    location.search,
    enqueueSnackbar,
    apiResponseData?.patient?.id,
    apiResponseData?.patient?.email
  ]);

  // Revoke object URLs on unmount
  useEffect(() => {
    return () => {
      if (insurancePreviews.front) URL.revokeObjectURL(insurancePreviews.front);
      if (insurancePreviews.back) URL.revokeObjectURL(insurancePreviews.back);
    };
  }, [insurancePreviews]);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const navigateToPortalLogin = (): void => {
    sessionStorage.setItem(GUARANTOR_INFO_RETURN_PATH_KEY, guarantorInfoReturnPath);
    history.push(portalLoginUrl);
  };

  const navigateToGuarantorHome = (): void => {
    dispatch(_setPatientPaymentsToken(paymentTokenForDashboard));
    dispatch(_guarantorLogin(true));
    history.push(
      `/${NavRoutes.PUBLIC.PAYMENTS_DASHBOARD}/${paymentTokenForDashboard}`
    );
  };

  const logoutFromPortal = (): void => {
    logoutGuarantor();
    dispatch(_guarantorLogin(false));
    dispatch(_guarantorLogout());
    sessionStorage.removeItem(GUARANTOR_INFO_PORTAL_LOGIN_KEY);
    sessionStorage.removeItem(GUARANTOR_INFO_PAYMENT_TOKEN_KEY);
    sessionStorage.removeItem(GUARANTOR_INFO_RETURN_PATH_KEY);
    history.push(
      `/${NavRoutes.PUBLIC.PAYMENTS_LOGIN}/pay/${paymentTokenForDashboard}`
    );
  };

  const clearInsuranceFile = (side: 'front' | 'back') => {
    setInsurancePreviews(current => {
      if (current[side]) {
        URL.revokeObjectURL(current[side] as string);
      }
      return {...current, [side]: null};
    });
    setInsuranceFiles(current => ({...current, [side]: null}));
    setInsuranceInputKeys(current => ({...current, [side]: current[side] + 1}));
  };

  const updateInsuranceFile = (side: 'front' | 'back', file: File | null) => {
    if (!file) {
      clearInsuranceFile(side);
      return;
    }
    setInsurancePreviews(current => {
      if (current[side]) {
        URL.revokeObjectURL(current[side] as string);
      }
      return {...current, [side]: URL.createObjectURL(file)};
    });
    setInsuranceFiles(current => ({...current, [side]: file}));
  };

  const useGuarantorEmail = Boolean(watch('use_guarantor_email'));
  const patientFullName = HelperUtils.formFullName(
    apiResponseData?.patient?.first_name,
    apiResponseData?.patient?.last_name,
    apiResponseData?.patient?.middle_name
  );

  const buildPayload = (formValues: GuarantorInfoFields) => {
    const copyGuarantorEmail = Boolean(formValues.use_guarantor_email);
    const patientEmail = copyGuarantorEmail
      ? (formValues.email || '').trim()
      : (formValues.patient_email || '').trim();
    const flat = {
      address_line_1: formValues.address_line_1,
      address_line_2: formValues.address_line_2,
      city: formValues.city,
      state: formValues.state,
      zip_code: formValues.zip_code,
      gender: formValues.gender,
      email: formValues.email,
      patient_email: patientEmail,
      use_guarantor_email: copyGuarantorEmail,
      dob: formValues.dob ? formValues.dob.format('YYYY-MM-DD') : '',
      is_guarantor_info_submitted: true
    };

    if (!insuranceFiles.front || !insuranceFiles.back) {
      return flat;
    }
    const payload = new FormData();
    (Object.keys(flat) as Array<keyof typeof flat>).forEach(key => {
      const value = (flat as any)[key];
      payload.append(key, typeof value === 'boolean' ? String(value) : value || '');
    });
    if (insuranceFiles.front) payload.append('insurance_file1', insuranceFiles.front);
    if (insuranceFiles.back) payload.append('insurance_file2', insuranceFiles.back);
    return payload;
  };

  // ─── Submit ───────────────────────────────────────────────────────────────

  const onSubmit: SubmitHandler<GuarantorInfoFields> = async (values) => {
    // Insurance partial-upload guard (not a field error, show snackbar)
    if (
      (insuranceFiles.front && !insuranceFiles.back) ||
      (!insuranceFiles.front && insuranceFiles.back)
    ) {
      enqueueSnackbar('Upload both sides of your insurance card, or remove uploaded files.', {
        variant: 'error'
      });
      return;
    }

    setSaving(true);
    try {
      const response = await saveGuarantorInfoForm({token, payload: buildPayload(values)});
      setSaved(true);
      enqueueSnackbar(response?.message, {variant: 'success'});
    } catch (error) {
      enqueueSnackbar(error?.message || 'Unable to save details.', {variant: 'error'});
    } finally {
      setSaving(false);
    }
  };

  // ─── Shared ───────────────────────────────────────────────────────────────

  // const renderLogo = (): ReactElement => <ClinicGroupsIcon className="practice-icon" />;
  const getSupportEmail = (): string => environment.supportEmail;

  function renderPortalHosTitle() {
    if (apiResponseData?.clinic_id) return apiResponseData.clinic_details.name;
    return apiResponseData?.clinic_group_details?.group_name;
  }

  function renderAddressLine1() {
    if (apiResponseData?.clinic_id) {
      return HelperUtils.formatFullAddress({
        line1: apiResponseData.clinic_details?.address,
        line2: apiResponseData.clinic_details?.address_line_2 || '',
        city: '', state: '', country: '', zip: ''
      });
    }
    return HelperUtils.formatFullAddress({
      line1: apiResponseData?.clinic_group_details?.address,
      line2: apiResponseData?.clinic_group_details?.address_line_2 || '',
      city: '', state: '', country: '', zip: ''
    });
  }

  function renderAddressLine2() {
    if (apiResponseData?.clinic_id) {
      return HelperUtils.formatFullAddress({
        line1: '', line2: '',
        city: apiResponseData?.clinic_details?.city,
        state: apiResponseData?.clinic_details?.state,
        country: '',
        zip: (apiResponseData?.clinic_details?.zip || '').toString()
      });
    }
    return HelperUtils.formatFullAddress({
      line1: '', line2: '',
      city: apiResponseData?.clinic_group_details?.city || '',
      state: apiResponseData?.clinic_group_details?.state || '',
      zip: (apiResponseData?.clinic_group_details?.zip || '').toString()
    });
  }

  // ─── Early returns ────────────────────────────────────────────────────────

  const renderPracticePhones = (): ReactNode => {
    const clinicDetails = apiResponseData?.clinic_details;
    if (!clinicDetails) {
      return null;
    }

    const formatPhone = (phone: string): string =>
      phone.startsWith('+1') ? phone : `+1${phone}`;

    if (clinicDetails.phone === clinicDetails.billing_phone) {
      return clinicDetails.phone ? (
        <div className="clinic-phone">
          {t('PAYMENTS_LOGIN.PHONE_QUERY_LABEL')}
          <PhoneFormatter value={formatPhone(clinicDetails.phone)} />
        </div>
      ) : null;
    }

    return (
      <>
        {clinicDetails.phone ? (
          <div className="clinic-phone">
            {t('PAYMENTS_LOGIN.PHONE')}
            <PhoneFormatter value={formatPhone(clinicDetails.phone)} />
          </div>
        ) : null}
        {clinicDetails.billing_phone ? (
          <div className="clinic-phone">
            {t('PAYMENTS_LOGIN.BILLING_PHONE')}
            <PhoneFormatter value={formatPhone(clinicDetails.billing_phone)} />
          </div>
        ) : null}
      </>
    );
  };

  const renderAfterContent = (): ReactElement => (
    <div className="payment-login-after-content">
      <section className="flex-centering contact-section">
        <p className="contact-us-text">
          <Trans
            i18nKey="GUARANTOR_PORTAL.LOGIN_SCREEN.CONTACT_INFO2"
            values={{supportEmail: getSupportEmail()}}
            components={[<Link href={`mailto:${getSupportEmail()}`} key="support-email" />]}
          />
        </p>
      </section>

      <div className="tag-wrapper flex-centering">
        <p className="tag-text">
          <Trans
            i18nKey="GUARANTOR_PORTAL.LOGIN_SCREEN.APP_CLOSING_INFORMATION_PART3"
            components={[<span key="closing-information" />]}
          />
        </p>
        <p className="tag-text-2">
          {t('GUARANTOR_PORTAL.LOGIN_SCREEN.APP_CLOSING_INFORMATION_PART2')}
        </p>
        <Link href={`${environment.websiteDetails.name}`} target="pcl">
          <MedopsLogo className="logo" />
        </Link>
      </div>
    </div>
  );

  const renderPageLayout = (
    children: ReactNode,
    options?: {contentLayout?: 'card' | 'plain'; className?: string}
  ): ReactElement => (
    <PublicPageLayout
      className={[
        'guarantor-info-page',
        'payment-login-page',
        options?.className
      ]
        .filter(Boolean)
        .join(' ')}
      testId="pcl-guarantor-infoPage"
      showHero={false}
      contentLayout={options?.contentLayout ?? 'card'}
      practiceBadgeLabel={apiResponseData ? renderPortalHosTitle() : undefined}
      // practiceBadgeIcon={apiResponseData ? renderLogo() : undefined}
      practiceName={apiResponseData ? renderPortalHosTitle() : undefined}
      addressLine1={apiResponseData ? renderAddressLine1() : undefined}
      addressLine2={apiResponseData ? renderAddressLine2() : undefined}
      practicePhones={apiResponseData ? renderPracticePhones() : undefined}
      showPracticeInfo={Boolean(apiResponseData)}
      afterContent={apiResponseData ? renderAfterContent() : undefined}>
      {children}
    </PublicPageLayout>
  );

  const renderSuccessScreen = (): ReactElement =>
    renderPageLayout(
      <div className="panel login-panel guarantor-info-form-panel guarantor-info-status-panel">
        <section className="guarantor-info-panel guarantor-info-success">
          <Typography variant="h5">Details submitted</Typography>
          <Typography>
            Thanks — your information has been submitted securely.
          </Typography>
          {fromPortalLogin && (
            <div className="guarantor-info-success-actions">
              <Button
                color="primary"
                variant="contained"
                onClick={navigateToGuarantorHome}>
                Go to Home Page
              </Button>
              <Button
                color="primary"
                variant="outlined"
                onClick={logoutFromPortal}>
                Logout
              </Button>
            </div>
          )}
        </section>
      </div>,
      {className: 'guarantor-info-status-page'}
    );

  if (loading) {
    return (
      <div className="flex-centering payment-login-loader">
        <Loader size={56} color="primary" />
      </div>
    );
  }

  if (pageError) {
    return renderPageLayout(
      <div className="panel login-panel">
        <section className="guarantor-info-panel">
          <Typography variant="h5">This link could not be opened</Typography>
          <Typography>{pageError}</Typography>
        </section>
      </div>
    );
  }

  if (alreadySubmitted) {
    const patientName = [
      apiResponseData?.patient?.first_name,
      apiResponseData?.patient?.last_name
    ]
      .filter(Boolean)
      .join(' ');
    const clinicName = apiResponseData?.clinic_id
      ? apiResponseData?.clinic_details?.name
      : apiResponseData?.clinic_group_details?.group_name;

    return renderPageLayout(
      <div className="panel login-panel guarantor-info-form-panel guarantor-info-status-panel">
        <section className="guarantor-info-panel guarantor-info-already-submitted">
          <Typography variant="h5">Already submitted</Typography>
          <Typography>
            {patientName
              ? `Guarantor information for ${patientName} has already been submitted.`
              : 'Your guarantor information has already been submitted.'}
          </Typography>
          <Typography>
            No further action is needed on this link. If you need to update your
            details, please contact {clinicName || 'your clinic'}.
          </Typography>
        </section>
      </div>,
      {className: 'guarantor-info-status-page'}
    );
  }

  if (saved) {
    return renderSuccessScreen();
  }

  // ─── Insurance upload ─────────────────────────────────────────────────────

  const renderInsuranceUploadSide = (side: 'front' | 'back'): JSX.Element => {
    const label = side === 'front' ? 'Front' : 'Back';
    const inputId = `insurance-${side}`;
    const preview = insurancePreviews[side];
    const file = insuranceFiles[side];

    return (
      <div className="guarantor-info-file-field">
        <Typography component="label" htmlFor={inputId}>
          {label}
        </Typography>
        <div className="guarantor-info-file-control">
            <Button
              component="label"
              variant="outlined"
              color="primary"
              startIcon={<PhotoCameraIcon />}>
              {file ? 'Change' : 'Upload or take photo'}
              <input
                key={`${inputId}-${insuranceInputKeys[side]}`}
                hidden
                id={inputId}
                type="file"
                accept="image/*,application/pdf"
                onChange={e => updateInsuranceFile(side, e.target.files?.[0] || null)}
              />
            </Button>
          {preview && (
            <div className="guarantor-info-file-preview-wrap">
              <img
                src={preview}
                alt={`${label} preview`}
                className="guarantor-info-file-preview"
              />
                <button
                  type="button"
                  className="guarantor-info-file-remove"
                  aria-label={`Remove ${label} insurance card`}
                  onClick={() => clearInsuranceFile(side)}>
                  <CloseIcon fontSize="small" />
                </button>
            </div>
          )}
          {file && <span className="guarantor-info-file-name">{file.name}</span>}
        </div>
      </div>
    );
  };

  // ─── Main form ────────────────────────────────────────────────────────────

  const renderForm = (): JSX.Element => (
    <form className="guarantor-info-form" onSubmit={handleSubmit(onSubmit)}>

      {/* Heading */}
      <div
        className={`guarantor-info-heading${
          fromPortalLogin ? ' guarantor-info-heading--no-margin-bottom' : ''
        }`}>
          <Typography variant="h4">Guarantor Information</Typography>

          {!fromPortalLogin && (
            <>
              <Typography className="guarantor-info-portal-login-hint">
                {`You're listed as the guarantor for an upcoming appointment. To protect
                your privacy, we will not pre-fill sensitive fields. Please complete
                the form so we can verify insurance and process billing before the
                visit.`}
              </Typography>

              <Typography className="guarantor-info-portal-login-hint">
                To verify your identity and access your patient information, please
                provide your first name, last name, and date of birth. You may review
                and update your details at any time without creating an account.{" "}
                <Link
                  component="button"
                  type="button"
                  className="guarantor-info-portal-login-link"
                  onClick={navigateToPortalLogin}
                >
                  Click here to get started.
                </Link>
              </Typography>
            </>
          )}
      </div>

      {/* ── Address ── */}
      <Typography variant="overline" className="guarantor-info-section-label">
        Address
      </Typography>

      {/* Address line 1 */}
      <div className="guarantor-info-field-label">Address line 1 *</div>
      <FormControl error={!!errors.address_line_1} fullWidth variant="outlined">
        <Controller
          name="address_line_1"
          control={control}
          defaultValue=""
          as={PaymentsStyledInput}
          placeholder="Street address, P.O. box"
          error={!!errors.address_line_1}
          style={{height: '2.5rem'}}
        />
        {errors.address_line_1 && (
          <FormHelperText>{errors.address_line_1.message}</FormHelperText>
        )}
      </FormControl>

      {/* Address line 2 */}
      <div className="guarantor-info-field-label">Address line 2</div>
      <FormControl fullWidth variant="outlined">
        <Controller
          name="address_line_2"
          control={control}
          defaultValue=""
          as={PaymentsStyledInput}
          placeholder="Apt, suite, unit (optional)"
          style={{height: '2.5rem'}}
        />
      </FormControl>

      <div className="guarantor-info-row guarantor-info-row--address">
        {/* City */}
        <div>
          <div className="guarantor-info-field-label">City *</div>
          <FormControl error={!!errors.city} variant="outlined" fullWidth>
            <Controller
              name="city"
              control={control}
              defaultValue=""
              as={PaymentsStyledInput}
              placeholder="City"
              error={!!errors.city}
              style={{height: '2.5rem'}}
            />
            {errors.city && (
              <FormHelperText>{errors.city.message}</FormHelperText>
            )}
          </FormControl>
        </div>

        {/* State */}
        <div>
          <div className="guarantor-info-field-label">State *</div>
          <FormControl error={!!errors.state} variant="outlined" fullWidth>
            <Controller
              name="state"
              control={control}
              defaultValue=""
              render={({onChange, value}) => (
                <Select
                  value={value || ''}
                  onChange={e => onChange(e.target.value)}
                  displayEmpty
                  style={{height: '2.5rem'}}>
                  <MenuItem value="" disabled>
                    Select
                  </MenuItem>
                  {statesList.map((item, index) => (
                    <MenuItem value={item.name} key={`${item.name}-${index}`}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {errors.state && (
              <FormHelperText>{errors.state.message}</FormHelperText>
            )}
          </FormControl>
        </div>

        {/* ZIP */}
        <div>
          <div className="guarantor-info-field-label">ZIP code *</div>
          <FormControl error={!!errors.zip_code} variant="outlined" fullWidth>
            <Controller
              name="zip_code"
              control={control}
              defaultValue=""
              as={PaymentsStyledInput}
              placeholder="ZIP code"
              inputProps={{inputMode: 'numeric', maxLength: 5}}
              error={!!errors.zip_code}
              style={{height: '2.5rem'}}
            />
            {errors.zip_code && (
              <FormHelperText>{errors.zip_code.message}</FormHelperText>
            )}
          </FormControl>
        </div>
      </div>

      {/* ── Personal details ── */}
      <Typography variant="overline" className="guarantor-info-section-label">
        Personal details
      </Typography>

      <div className="guarantor-info-row">
        {/* Date of birth */}
        <div style={{flex: 1}}>
          <div className="guarantor-info-field-label">Date of birth *</div>
          <FormControl error={!!errors.dob} fullWidth variant="outlined">
            <Controller
              name="dob"
              control={control}
              render={({onChange, onBlur, value}) => (
                <DateTimePickerWrapper
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder="MM/DD/YYYY"
                  error={!!errors.dob}
                  style={{height: '2.5rem'}}
                />
              )}
            />
            {errors.dob && (
              <FormHelperText>{(errors.dob as any).message}</FormHelperText>
            )}
          </FormControl>
        </div>

        {/* Sex */}
        <div style={{flex: 1}}>
          <div className="guarantor-info-field-label">
            Sex (as listed on insurance) *
          </div>
          <FormControl error={!!errors.gender} variant="outlined" fullWidth>
            <Controller
              name="gender"
              control={control}
              defaultValue=""
              render={({onChange, value}) => (
                <Select
                  value={value}
                  onChange={e => onChange(e.target.value)}
                  displayEmpty
                  style={{height: '2.5rem'}}>
                  <MenuItem value="" disabled>
                    Select
                  </MenuItem>
                  {sexOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {errors.gender && (
              <FormHelperText>{errors.gender.message}</FormHelperText>
            )}
          </FormControl>
        </div>
      </div>

      <div className="guarantor-info-field-label">
        Guarantor email{useGuarantorEmail ? ' *' : ''}
      </div>
      <FormControl error={!!errors.email} fullWidth variant="outlined">
        <Controller
          name="email"
          control={control}
          defaultValue=""
          as={PaymentsStyledInput}
          placeholder={useGuarantorEmail ? 'Guarantor email' : 'Guarantor email (optional)'}
          inputProps={{inputMode: 'email', autoComplete: 'email'}}
          error={!!errors.email}
          style={{height: '2.5rem'}}
        />
        {errors.email && (
          <FormHelperText>{errors.email.message}</FormHelperText>
        )}
      </FormControl>

      <FormControlLabel
        style={{marginTop: 8, marginBottom: 4}}
        control={
          <Controller
            name="use_guarantor_email"
            control={control}
            defaultValue={false}
            render={({onChange, value}) => (
              <Checkbox
                color="primary"
                checked={Boolean(value)}
                onChange={event => {
                  const checked = event.target.checked;
                  onChange(checked);
                  if (checked) {
                    setValue('patient_email', watch('email') || '');
                  }
                }}
              />
            )}
          />
        }
        label={
          patientFullName ? (
            <span>
              Use the guarantor&apos;s email for <strong>{patientFullName}</strong>
            </span>
          ) : (
            "Use the guarantor's email for the patient"
          )
        }
      />

      {!useGuarantorEmail && (
        <>
          <div className="guarantor-info-field-label">Patient email *</div>
          <FormControl error={!!errors.patient_email} fullWidth variant="outlined">
            <Controller
              name="patient_email"
              control={control}
              defaultValue=""
              as={PaymentsStyledInput}
              placeholder="Patient email"
              inputProps={{inputMode: 'email', autoComplete: 'email'}}
              error={!!errors.patient_email}
              style={{height: '2.5rem'}}
            />
            {errors.patient_email && (
              <FormHelperText>{errors.patient_email.message}</FormHelperText>
            )}
          </FormControl>
        </>
      )}

      {/* ── Insurance card ── */}
        <div className="guarantor-info-insurance">
          <Typography
            variant="overline"
            className="guarantor-info-section-label">
            Insurance card
          </Typography>

          <Typography variant="body2" className="guarantor-info-insurance-desc">
            {hasInsurance
              ? 'Your insurance card images on file are shown below.'
              : 'Upload clear photos of both sides of the insurance card. We will use these to verify your benefits before the appointment.'}
          </Typography>

          {!hasInsurance && (
            <div className="guarantor-info-insurance-tip">
              <Typography variant="caption">
                💡 Place the card on a dark, flat surface in good lighting. Make
                sure all text is readable.
              </Typography>
            </div>
          )}

          {loadingInsurancePreviews ? (
            <div className="guarantor-info-insurance-loading">
              <Loader size={32} color="primary" />
            </div>
          ) : (
            <div className="guarantor-info-row">
              {renderInsuranceUploadSide('front')}
              {renderInsuranceUploadSide('back')}
            </div>
          )}
        </div>

      {/* ── Submit ── */}
      <div className="guarantor-info-actions">
        <Button
          color="primary"
          variant="contained"
          type="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={saving}
          size="large"
          fullWidth>
          {saving ? 'Submitting…' : 'Save & Submit'}
        </Button>
      </div>
    </form>
  );

  // ─── Page shell ───────────────────────────────────────────────────────────

  return renderPageLayout(
    <div className="panel login-panel guarantor-info-form-panel">{renderForm()}</div>
  );
};

export default GuarantorInfo;